import type { StreamingSseStatus } from '@/hooks/use-streaming-sse'
import { cn } from '@/lib/utils'

export type ConnectionStatusBadgeProps = {
  status: StreamingSseStatus
  lastEventAt?: number | null
  className?: string
}

export function ConnectionStatusBadge({
  status,
  lastEventAt = null,
  className,
}: ConnectionStatusBadgeProps) {
  const meta =
    status === 'open' && lastEventAt
      ? `Updated ${new Date(lastEventAt).toLocaleTimeString()}`
      : null

  const { label, className: toneClass } = (() => {
    switch (status) {
      case 'connecting':
        return {
          label: 'Connecting…',
          className: 'bg-warning/15 text-warning border-warning/40',
        }
      case 'open':
        return {
          label: 'Live',
          className: 'bg-success/15 text-success border-success/40',
        }
      case 'reconnecting':
        return {
          label: 'Reconnecting…',
          className: 'bg-warning/15 text-warning border-warning/40',
        }
      case 'closed':
        return {
          label: 'Offline',
          className: 'bg-muted text-muted-foreground border-border',
        }
      case 'idle':
      default:
        return {
          label: 'Idle',
          className: 'bg-muted text-muted-foreground border-border',
        }
    }
  })()

  return (
    <div
      className={cn('inline-flex flex-col gap-0.5', className)}
      title={meta ?? undefined}
    >
      <span
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium',
          toneClass
        )}
      >
        <span
          className={cn(
            'size-1.5 rounded-full',
            status === 'open' && 'bg-success',
            (status === 'connecting' || status === 'reconnecting') &&
              'bg-warning animate-pulse',
            (status === 'idle' || status === 'closed') && 'bg-muted-foreground/50'
          )}
          aria-hidden
        />
        {label}
      </span>
    </div>
  )
}
